export type SignInProvider = 'google' | 'microsoft';

export const PROVIDER_LABELS: Record<SignInProvider, string> = {
  google: 'Google',
  microsoft: 'Microsoft',
};

const AUTH_ERROR_MESSAGES: Record<string, string> = {
  account_exists_link_required: 'A parent account already uses this email. Sign in with your password first, then connect Google or Microsoft from Account.',
  email_unavailable: 'That account did not share an email address with us. Try another account or use email and password.',
  provider_already_linked: 'Your parent account is already connected to a different account from this provider.',
  identity_in_use: 'That account is already connected to a different Student Assistant login.',
  sign_in_cancelled: 'Sign-in was cancelled.',
  sign_in_expired: 'That sign-in attempt expired. Please try again.',
  sign_in_incomplete: 'Sign-in did not finish. Please try again.',
  sign_in_failed: 'We could not confirm your account with the provider. Please try again.',
  sign_in_required: 'Sign in first, then connect another account from Account.',
  provider_unavailable: 'That sign-in option is not available right now.',
  rate_limited: 'Too many attempts. Wait a minute and try again.',
  link_failed: 'We could not connect that account. Please try again.',
};

/** Reads and clears `?auth_error=` left by the OIDC callback redirect. */
export function consumeAuthError(): string | null {
  const params = new URLSearchParams(window.location.search);
  const code = params.get('auth_error');
  if (!code) return null;
  params.delete('auth_error');
  const query = params.toString();
  window.history.replaceState({}, '', `${window.location.pathname}${query ? `?${query}` : ''}`);
  return AUTH_ERROR_MESSAGES[code] ?? 'Sign-in could not be completed. Please try again.';
}

export function ProviderIcon({ provider }: { provider: SignInProvider }) {
  if (provider === 'google') {
    return (
      <svg className="provider-icon" viewBox="0 0 48 48" aria-hidden="true" focusable="false">
        <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z" />
        <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" />
        <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-7.9l-6.5 5C9.5 39.6 16.2 44 24 44z" />
        <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z" />
      </svg>
    );
  }
  return (
    <svg className="provider-icon" viewBox="0 0 21 21" aria-hidden="true" focusable="false">
      <rect x="1" y="1" width="9" height="9" fill="#F25022" />
      <rect x="11" y="1" width="9" height="9" fill="#7FBA00" />
      <rect x="1" y="11" width="9" height="9" fill="#00A4EF" />
      <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
    </svg>
  );
}

/**
 * Full-page navigation (not fetch): the provider redirect must happen at the top level.
 */
export function SocialSignInButtons({ providers, verb = 'Continue', intent }: {
  providers: SignInProvider[];
  verb?: string;
  intent?: 'link';
}) {
  if (!providers.length) return null;
  return (
    <div className="social-signin">
      {providers.map((provider) => (
        <a key={provider} className="social-button"
          href={`/api/auth/oidc/${provider}/start${intent ? `?intent=${intent}` : ''}`}>
          <ProviderIcon provider={provider} />
          <span>{verb} with {PROVIDER_LABELS[provider]}</span>
        </a>
      ))}
    </div>
  );
}

export function AuthDivider({ label }: { label: string }) {
  return <div className="auth-divider" role="separator"><span>{label}</span></div>;
}
